<template>
  <div class="content-wrapper">
    <h2 class="page-titles">Orders</h2>

    <div class="search-export pt-6">
      <div class="flex items-center">
        <div class="w-1/3 relative">
          <Search placeholder="Search by order#, name" @input="handleSearch" />
        </div>
        <div class="ml-auto">
          <button
            @click="exportOrders"
            :class="{ 'button-loading': exportingOrders }"
            :disabled="!orders.length || exportingOrders"
            class="whitebutton-gemnote button-gemnote-glass--tiny "
          >
            <span>Export </span>
          </button>
        </div>
      </div>
    </div>

    <div class="order-summary mt-4">
      <div class="flex mt-9  md:flex-row flex-col">
        <div class="card-holder md:w-1/2 w-full p-5 border-right-mobile">
          <div class="flex justify-between">
            <div>
              <div class="flex gap-2 items-center">
                <span class="font-bold">Total Revenue</span>
              </div>
            </div>
          </div>
          <div
            class="mt-2.5 text-left flex items-center gap-1"
            v-if="loadingOrders"
          >
            <Skeleton width="20%" height="1.8rem" style="margin-bottom: 0rem" />
            <Skeleton width="15%" height="1.8rem" style="margin-bottom: 0rem" />
          </div>
          <div class="mt-2.5 text-left flex items-center gap-1" v-else>
            <span class="text-2xl font-semibold value">{{
              formatCurrency(shopOrders.metrics.totalRevenue)
            }}</span>
            <span class="text-xl font-light">USD</span>
          </div>
        </div>

        <div class="card-holder  p-5 md:w-1/2 w-full border-right">
          <div class="flex justify-between">
            <div>
              <div class="flex gap-2 items-center">
                <span class="font-bold">Net Payout</span>
              </div>
            </div>
          </div>
          <div
            class="mt-2.5 text-left flex items-center gap-1"
            v-if="loadingOrders"
          >
            <Skeleton width="20%" height="1.8rem" style="margin-bottom: 0rem" />
            <Skeleton width="15%" height="1.8rem" style="margin-bottom: 0rem" />
          </div>
          <div class="mt-2.5 text-left flex items-center gap-1" v-else>
            <span class="text-2xl font-semibold value">
              <span class="value">{{
                formatCurrency(shopOrders.metrics.netPayout)
              }}</span>
              <span class="text-xl font-light"> USD</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="filter flex mt-6">
      <div class="date-filter flex items-center justify-end gap-2 ml-auto">
        <div class="date-filter-title flex items-center gap-2">
          <img src="../../assets/filter-icon.svg" />
          <span class="font-normal text-sm">Date</span>
        </div>
        <el-select v-model="dateFilter" placeholder="Select">
          <el-option
            v-for="(filter, key) in dateFilters"
            :key="key"
            :label="filter.label"
            :value="filter.value"
          >
          </el-option>
        </el-select>
      </div>
    </div>

    <div class="mt-5">
      <vue-good-table
        :isLoading="loadingOrders"
        :styleClass="
          `vgt-table striped order-table ${
            loadingOrders ? 'vgt-table--loading' : ''
          }`
        "
        :columns="columns"
        :rows="orders"
        mode="remote"
        :totalRows="ordersPagination.totalCount"
        @on-page-change="handlePageChange"
        :pagination-options="{
          enabled: true,
          perPageDropdownEnabled: false
        }"
      >
        <template slot="loadingContent">
          <div class="">
            <Skeleton width="100%" height="3rem" v-for="i in 5" :key="i" />
          </div>
        </template>

        <template slot="table-row" slot-scope="orders">
          <template v-if="orders.column.field === 'details'">
            <router-link
              :to="'/e-commerce/orders/' + orders.row.uuid"
              class="font-bold cursor-pointer"
              >View Details</router-link
            >
          </template>

          <span v-else-if="orders.column.field === 'status'">
            {{ orders.row.status | capitalize }}
          </span>
        </template>

        <template slot="pagination-bottom">
          <Pagination
            :total-items="ordersPagination.totalCount"
            :page="serverParams.page"
            :items-per-page="serverParams.perPage"
            @page-change="handlePageChange"
            @perPageChange="handlePerPageChange"
          />
        </template>
      </vue-good-table>
    </div>

    <!-- Modal Here -->
    <ExportModal
      :showFooter="true"
      v-show="isExportModalActive"
      @hideModal="closeModal"
      :showCancelButton="false"
      @confirmAction="closeModal"
    >
      <template slot="header">
        <span>Exporting Orders</span>
      </template>

      <template slot="body">
        <span
          >We are exporting your order, we will send a link to
          <strong> {{ user.email }} </strong> when it’s done. Keep an eye on
          your inbox!
        </span>
      </template>
    </ExportModal>
  </div>
</template>

<script>
import { VueGoodTable } from 'vue-good-table'
import { mapState } from 'vuex'
import Util from '../../mixins/Util'
import Skeleton from '../Universal/Skeleton'
import Pagination from '@/components/Universal/Pagination'
import Search from '@/components/Universal/Search'
import ExportModal from '@/components/Universal/ExportModal'
import _ from 'lodash'

export default {
  name: 'EcommerceOrders',
  mixins: [Util],
  components: {
    ExportModal,
    VueGoodTable,
    Skeleton,
    Pagination,
    Search
  },
  data: () => ({
    serverParams: {
      page: 0,
      perPage: 10
    },
    dateFilters: [
      {
        label: 'Today',
        value: 'today'
      },
      {
        label: 'Yesterday',
        value: 'yesterday'
      },
      {
        label: 'Last 7 days',
        value: 'last_7'
      },
      {
        label: 'Last 30 days',
        value: 'last_30'
      },
      {
        label: 'All time',
        value: ''
      }
    ],
    dateFilter: 'today',
    columns: [
      {
        label: 'Date',
        field: 'date'
      },
      {
        label: 'Order #',
        field: 'order'
      },
      {
        label: 'Name',
        field: 'name'
      },
      {
        label: 'Status',
        field: 'status'
      },
      {
        label: 'Items',
        field: 'items'
      },
      {
        label: 'Total',
        field: 'total'
      },
      {
        label: 'Details',
        field: 'details',
        tdClass: 'shop-view-details-cols'
      }
    ]
  }),
  computed: {
    ...mapState({
      shopOrders: state => state.ecommerce.shopOrders,
      loadingOrders: state => state.ecommerce.loadingOrders,
      exportingOrders: state => state.ecommerce.exportingOrder,
      isExportModalActive: state => state.modal.isExportModalActive,
      user: state => state.profile.user
    }),

    ordersPagination() {
      return this.shopOrders.pagination
    },
    orders() {
      return this.shopOrders.orders.map(order => {
        return {
          uuid: order.attributes.uuid,
          date: this.formatDate(order.attributes.createdAt),
          order: order.id,
          name: order.attributes.recipientName,
          status: order.attributes.status,
          items: order.attributes.cartLineItemsSize,
          total: this.formatCurrency(order.attributes.total || 0)
        }
      })
    }
  },
  created() {
    this.getOrders()
  },
  methods: {
    exportOrders() {
      this.$store.dispatch('modal/handleExports', 'e_commerce_orders')
    },
    handlePageChange(page) {
      this.serverParams.page = page
      this.getOrders(this.serverParams.page + 1)
    },
    handlePerPageChange(e) {
      this.serverParams.perPage = e
      this.getNextChuckOfOrders()
    },
    getOrders(page = 1) {
      this.$store.dispatch('ecommerce/getOrderList', {
        page,
        perPage: this.serverParams.perPage,
        time_period: this.dateFilter
      })
    },

    getNextChuckOfOrders() {
      this.$store.dispatch('ecommerce/getOrderList', {
        page: this.serverParams.page + 1,
        perPage: this.serverParams.perPage,
        time_period: this.dateFilter
      })
    },
    handleSearch: _.debounce(function(query) {
      if (query) {
        this.$store.dispatch('ecommerce/searchOrders', { query })
      } else {
        this.getOrders()
      }
    }, 1000),

    closeModal() {
      this.$store.dispatch('modal/setExportModal', false)
    }
  },

  watch: {
    dateFilter() {
      this.getNextChuckOfOrders()
    }
  }
}
</script>

<style lang="scss" scoped>
.button-gemnote-glass--tiny {
  width: auto;
  height: 2.4rem;
  padding: 0 1.5rem !important;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

input {
  &::placeholder {
    color: #6c7a7b;
    font-size: 0.875rem;
  }
}

.border-2 {
  border: 1px solid #232220;
}

.border-l-2 {
  border-left: 1px solid #232220;
}

.value {
  color: #24292c;
  font-size: 1.375rem;
}

.date-filter {
  width: 100%;
}

.date-filter-title {
  span {
    font-size: 0.835rem;
    font-weight: 600;
  }
}

.card-holder {
  border: 1px solid #232220;
  height: max-content;
  border-right: none;

  &.border-right {
    border-right: 1px solid #232220;
  }

  &.border-right-mobile {
    border-right: none;
  }

  @media (max-width: 768px) {
    &.border-right-mobile {
      border-right: 1px solid #232220;
      border-bottom: none;
    }
  }
}
</style>
